import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle,
  Confetti,
  CalendarBlank,
  Clock,
  Target,
  PushPin,
  CheckCircle,
  Lock,
  CaretDown,
  Chat,
  Package,
  Books,
  Trophy,
} from "@phosphor-icons/react";
import HighlightsCard from "../components/HighlightsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - now;
  if (isNaN(diff)) return null;
  const past = diff < 0;
  const abs = Math.abs(diff);
  const days = Math.floor(abs / 86400000);
  const hours = Math.floor((abs % 86400000) / 3600000);
  const minutes = Math.floor((abs % 3600000) / 60000);
  const seconds = Math.floor((abs % 60000) / 1000);
  return { past, days, hours, minutes, seconds };
}

function WeekAccordion({ week, isCurrent, isDone }) {
  const [open, setOpen] = useState(false);
  const isExhibition = week.week_num === 10;

  let style = "bg-white border-slate-200";
  if (isDone) style = "bg-emerald-50/60 border-emerald-200";
  if (isCurrent) style = "bg-yellow-50 border-yellow-300";
  if (isExhibition && !isDone && !isCurrent) style = "bg-gradient-to-br from-pink-50 to-amber-50 border-amber-200";

  return (
    <div
      className={`rounded-2xl border transition ${style}`}
      data-testid={`parent-week-${week.week_num}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`parent-week-toggle-${week.week_num}`}
        aria-expanded={open}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <div className="flex flex-col items-center justify-center w-14 shrink-0">
          <span className="text-[10px] font-bold tracking-wider uppercase text-slate-500">Week</span>
          <span className="font-outfit text-2xl font-black leading-none text-slate-900">
            {week.week_num}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-outfit font-bold text-lg leading-tight">{week.title}</h4>
            {isDone && (
              <CheckCircle weight="fill" size={16} className="text-emerald-500 shrink-0" />
            )}
            {isCurrent && (
              <span className="text-[10px] font-bold tracking-wider uppercase bg-yellow-400 text-yellow-950 px-2 py-0.5 rounded-full">
                Now
              </span>
            )}
          </div>
          <p className="font-caveat text-lg text-slate-600 mt-0.5">{week.tagline}</p>
          <p className="text-[11px] text-slate-500 mt-1 uppercase tracking-wider font-semibold">
            {week.category} · {week.duration}
          </p>
        </div>
        <CaretDown
          size={18}
          weight="bold"
          className={`text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div
              className="px-4 pb-5 space-y-4 border-t border-slate-200/60 pt-4"
              data-testid={`parent-week-body-${week.week_num}`}
            >
              {week.core_concept && (
                <p className="text-sm text-slate-700 leading-relaxed">{week.core_concept}</p>
              )}

              {/* Tasks */}
              {week.tasks?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <Target size={13} weight="bold" /> What your child will do
                  </div>
                  <ul className="space-y-1.5">
                    {week.tasks.map((t, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-slate-800"
                        data-testid={`parent-week-task-${week.week_num}-${i}`}
                      >
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sessions / class plan */}
              {week.sessions?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <Books size={13} weight="bold" /> In class
                  </div>
                  <ol className="space-y-1.5">
                    {week.sessions.map((s, i) => (
                      <li key={i} className="text-sm flex gap-2">
                        <span className="font-bold tracking-wider uppercase text-slate-500 w-14 shrink-0 text-[11px] pt-0.5">
                          {s.time}
                        </span>
                        <span>
                          <span className="font-semibold text-slate-900">{s.title}</span>
                          <span className="text-slate-600"> — {s.description}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Materials */}
              {week.materials?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <Package size={13} weight="bold" /> What to bring
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {week.materials.map((m, i) => (
                      <span
                        key={i}
                        className="text-xs bg-white border border-slate-200 rounded-lg px-2 py-1"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Success check */}
              {week.success_check && (
                <div className="bg-white/70 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                  <Trophy weight="fill" size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[11px] font-bold tracking-wider uppercase text-emerald-800">
                      Success check
                    </div>
                    <p className="text-sm text-slate-700">{week.success_check}</p>
                  </div>
                </div>
              )}

              {/* Conversation starters */}
              {week.conversation_starters?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                    <Chat size={13} weight="bold" /> Ask at home
                  </div>
                  <ul className="space-y-1.5">
                    {week.conversation_starters.map((q, i) => (
                      <li
                        key={i}
                        className="font-caveat text-lg text-yellow-900 bg-yellow-100/70 rounded-xl px-3 py-2"
                        data-testid={`parent-week-conv-${week.week_num}-${i}`}
                      >
                        "{q}"
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


export default function ParentPortal() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/public/class/${code}`)
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(
          e.response?.status === 404
            ? "Hmm, we couldn't find that class. Double-check the link with the teacher."
            : "Something went wrong loading this page."
        )
      );
  }, [code]);

  const thisWeek = useMemo(
    () => data?.weeks?.find((w) => w.week_num === data.current_week),
    [data]
  );
  const exhibition = useMemo(
    () => data?.weeks?.find((w) => w.week_num === 10),
    [data]
  );
  const countdown = useCountdown(data?.exhibition_date);

  if (error)
    return (
      <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <Lock size={40} className="text-slate-400 mx-auto mb-3" />
          <h1 className="font-outfit text-2xl font-bold" data-testid="parent-error">{error}</h1>
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center text-slate-500" data-testid="parent-loading">
        Loading...
      </div>
    );

  const heroBg =
    "https://static.prod-images.emergentagent.com/jobs/2d9c142e-ed55-40de-ac53-c13d9c2402dc/images/667d272cc959be0443256e780b204d23b6ef7ee8ecd743f7da322b5ccf810734.png";

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      {/* Header */}
      <header className="border-b border-slate-200 bg-[#FFFDF8]/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-yellow-400 flex items-center justify-center">
              <Sparkle weight="fill" className="text-yellow-950" size={20} />
            </div>
            <div>
              <div className="font-outfit font-black leading-none">Alpha AI Discovery Club</div>
              <div className="text-[11px] text-slate-500">Parent preview · {data.class_code}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 hidden sm:block">Read-only · Privacy first</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-caveat text-2xl text-yellow-700">Welcome, parent</p>
          <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight" data-testid="parent-class-name">
            {data.class_name}
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Here's what your child is learning — and what's coming next. Built by students, for the world.
          </p>
        </motion.div>

        {/* Countdown to Exhibition */}
        {data.exhibition_date && countdown && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-3xl overflow-hidden border border-amber-200"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(255,237,213,0.95), rgba(254,202,202,0.85))`,
            }}
            data-testid="parent-countdown"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <Confetti weight="fill" className="text-pink-500" size={22} />
                <span className="text-[11px] font-bold tracking-wider uppercase text-pink-700 bg-white/70 px-2 py-1 rounded-full">
                  {countdown.past ? "Exhibition complete" : "Exhibition Day countdown"}
                </span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
                {countdown.past ? "Thank you for coming!" : "Mark your calendar"}
              </h2>
              {!countdown.past && (
                <div className="mt-4 flex flex-wrap gap-3" data-testid="parent-countdown-blocks">
                  {[
                    { label: "days", value: countdown.days },
                    { label: "hours", value: countdown.hours },
                    { label: "min", value: countdown.minutes },
                    { label: "sec", value: countdown.seconds },
                  ].map((b) => (
                    <div
                      key={b.label}
                      className="bg-white/80 backdrop-blur rounded-2xl px-4 py-3 min-w-[84px] text-center border border-amber-200"
                    >
                      <div className="font-outfit text-3xl font-black tabular-nums text-slate-900">
                        {String(b.value).padStart(2, "0")}
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{b.label}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-700">
                <CalendarBlank size={16} weight="bold" />
                {new Date(data.exhibition_date).toLocaleString([], {
                  dateStyle: "full",
                  timeStyle: "short",
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* This Week Hero */}
        {thisWeek && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-3xl overflow-hidden shadow-sm hero-grain"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.78), rgba(15,23,42,0.55)), url(${heroBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            data-testid="parent-this-week"
          >
            <div className="p-6 sm:p-10 text-white relative">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 text-xs font-bold tracking-wider uppercase rounded-full bg-yellow-400 text-yellow-950">
                  This Week · Week {thisWeek.week_num}
                </span>
                <span className="text-xs text-white/70">{thisWeek.category}</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-5xl font-black tracking-tight max-w-3xl">
                {thisWeek.title}
              </h2>
              <p className="font-caveat text-2xl sm:text-3xl text-yellow-300 mt-2">{thisWeek.tagline}</p>
              <p className="mt-4 text-white/85 max-w-2xl leading-relaxed">{thisWeek.core_concept}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur px-3 py-1.5 rounded-xl text-xs">
                  <Clock size={14} weight="bold" /> {thisWeek.duration}
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur px-3 py-1.5 rounded-xl text-xs">
                  <Target size={14} weight="bold" /> {thisWeek.tasks?.length || 0} tasks
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* 10-Week Journey (expandable) */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="parent-timeline">
          <h3 className="font-outfit text-2xl font-bold tracking-tight mb-1">10-Week Journey</h3>
          <p className="text-sm text-slate-500 mb-4">
            Tap any week to see exactly what your child is learning, the activities they'll do in class, and questions you can ask at home.
          </p>
          <div className="space-y-2">
            {data.weeks.map((w) => (
              <WeekAccordion
                key={w.week_num}
                week={w}
                isCurrent={w.week_num === data.current_week}
                isDone={w.week_num < data.current_week}
              />
            ))}
          </div>
        </div>

        {/* Notes for parents */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <PushPin weight="fill" className="text-yellow-500" size={20} />
            <h3 className="font-outfit font-bold text-xl">From the teacher</h3>
          </div>
          {(data.parent_notes || []).length === 0 ? (
            <p className="text-sm text-slate-500 italic">No notes posted yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {data.parent_notes.map((a, idx) => (
                <div
                  key={a.id}
                  className="bg-yellow-100 rounded-2xl p-4"
                  style={{ transform: `rotate(${idx % 2 === 0 ? -0.5 : 0.5}deg)` }}
                  data-testid={`parent-note-${a.id}`}
                >
                  <p className="font-caveat text-2xl text-yellow-950 leading-tight">{a.message}</p>
                  <div className="text-[11px] text-yellow-900/70 mt-1">— {a.author_name}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Class highlights — public, read-only */}
        <HighlightsCard readOnly preloaded={data.highlights || []} />

        {/* Exhibition preview */}
        {exhibition && (
          <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Confetti weight="fill" className="text-pink-500" size={22} />
              <h3 className="font-outfit font-bold text-xl">What happens on Exhibition Day</h3>
            </div>
            <p className="text-slate-700 mb-4">{exhibition.core_concept}</p>
            <ol className="space-y-2">
              {exhibition.sessions.map((s, idx) => (
                <li key={idx} className="flex gap-3 text-sm">
                  <span className="font-bold tracking-wider uppercase text-slate-500 w-16 shrink-0">{s.time}</span>
                  <span>
                    <span className="font-outfit font-semibold">{s.title}</span>{" "}
                    <span className="text-slate-600">— {s.description}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <footer className="text-center text-xs text-slate-400 py-8">
          Built with care for the Alpha AI Discovery Club · Tema Community 25
        </footer>
      </main>
    </div>
  );
}
